import React from "react";
import { theme } from "antd";
import type { GlobalToken } from "antd";
import { useTranslate } from "@refinedev/core";
import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import styled from "styled-components";
import { Tooltip } from "@libertify/ui";

interface FileVisibilityToggleProps {
  hidden: boolean;
  onToggle: () => void;
}

/**
 * Eye toggle that flips a document between "shown in the experience" and
 * "knowledge-base only". Green when visible, neutral when hidden.
 */
export const FileVisibilityToggle: React.FC<FileVisibilityToggleProps> = ({
  hidden,
  onToggle,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  return (
    <Tooltip
      content={t(
        `createExperience.addOns.documents.visibility.${
          hidden ? "hiddenTip" : "visibleTip"
        }`
      )}
    >
      <VisibilityButton
        type="button"
        $token={token}
        $on={!hidden}
        aria-label={t(
          `createExperience.addOns.documents.visibility.${
            hidden ? "hiddenAria" : "visibleAria"
          }`
        )}
        onClick={onToggle}
      >
        {hidden ? (
          <EyeSlashIcon size={16} />
        ) : (
          <EyeIcon size={16} weight="fill" />
        )}
      </VisibilityButton>
    </Tooltip>
  );
};

// ─── Styled components ───────────────────────────────────────────────────────

const VisibilityButton = styled.button<{ $token: GlobalToken; $on: boolean }>`
  flex-shrink: 0;
  appearance: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${({ $token, $on }) => ($on ? $token.colorSuccessBorder : $token.colorBorder)};
  background: ${({ $token, $on }) =>
    $on ? $token.colorSuccessBg : $token.colorBgContainer};
  color: ${({ $token, $on }) =>
    $on ? $token.colorSuccess : $token.colorTextSecondary};
  transition: all ${({ $token }) => $token.motionDurationFast};
`;
